import Flickity from "flickity";
import "flickity-fade";
import { ResizeObserver as RoPolyfill } from "@juggle/resize-observer";
import Sb from "../abstract/StatefulBehavior";
import flickityOptions from "../../helpers/flickityOptions";
import Percolator from "../../helpers/Percolator";
import ObjectFit from "./ObjectFit";
import { focusable as domFocusable } from "../../helpers/utilities";

// Polyfill ResizeObserve only if unsupported (Safari, IE11)
const ResizeObserver = (window && window.ResizeObserver) || RoPolyfill;

export default class SlideBlocks extends Sb {
  constructor(el, props, refs) {
    super();

    this.props = props;
    this.refs = refs;

    this.state = {
      active: this.hashSlideIndex,
    };

    this.sliderEl = refs.slider || el;
    const options = {
      ...flickityOptions,
      lazyLoad: this.lazyLoad,
      fade: this.useFadeTransition,
      // 'ready' event must be bound with `on` option
      on: {
        ready: () => {
          if (props.variableHeight) refs.nav.classList.add("is-variable");
          this.setNavHeight();
        },
      },
    };

    // optionally instantiate ObjectFit behavior on slides
    // ensures behavior has run prior to Flickity instantiation so that
    // Flickity's inline styles on slides aren't overridden
    if (props.objectFit) {
      const percolator = new Percolator();
      const size = props.objectFitSize || "cover";

      this.images = [...this.sliderEl.children].map((child, index) => {
        const image = child.querySelector("img");
        if (!image) return;
        return percolator.loadBehavior(image, ObjectFit, { size: size });
      });
    }

    this.slider = new Flickity(this.sliderEl, options);

    this.resizeObserver = new ResizeObserver(this.setNavHeight);
    this.resizeObserver.observe(this.sliderEl);

    this.update();
    this.bindEvents();
  }

  get lazyLoad() {
    const { lazyload } = this.props;
    if (!lazyload) return flickityOptions.lazyLoad;
    if (lazyload === "true") return true;
    if (lazyload === "false") return false;
    return parseInt(lazyload, 10);
  }

  get slideCount() {
    return this.slider.slides.length || 0;
  }

  get slides() {
    const { slide } = this.refs;
    return Array.isArray(slide) ? slide : Array(slide);
  }

  get activeImg() {
    if (!this.refs.image || this.refs.image.length === 0) {
      return null;
    }
    return this.refs.image[this.state.active];
  }

  get activeSlide() {
    if (!this.slides || this.slides.length === 0) {
      return null;
    }
    return this.slides[this.state.active];
  }

  get hashSlideIndex() {
    const hash = location.hash;

    if (hash) {
      const slug = hash.slice(1, hash.length);
      const slide = this.slides.filter((slide) => {
        return slide.dataset.workSlug === slug;
      });
      return slide.length === 1 ? Number(slide[0].dataset.index) : 0;
    }

    return 0;
  }

  get useFadeTransition() {
    return this.props.useFadeTransition ?? false;
  }

  update = () => {
    const { current } = this.refs;
    const { active } = this.state;

    this.slider.select(active);
    this.postSlideChange();

    this.slides.forEach((slide, index) => {
      const focusable = domFocusable(slide);
      const isActive = index === active;
      focusable.forEach((el) => el.setAttribute("tabindex", isActive ? 0 : -1));
    });

    if (current) current.textContent = active + 1;
  };

  postSlideChange = () => {
    if (!this.props.postSlideChange) return;

    const slide = this.activeSlide;
    if (!slide) return;

    const workSlug = slide.dataset.workSlug;
    const groupId = slide.dataset.groupId;
    const workId = slide.dataset.workId;

    if (typeof window.postMessage !== "undefined") {
      window.postMessage(
        ["slide", { workId: workId, workSlug: workSlug, groupId: groupId }],
        "*"
      );
    }
  };

  bindEvents() {
    const { previousButton, nextButton } = this.refs;
    const { variableHeight } = this.props;

    previousButton.addEventListener("click", this.handlePreviousClick);
    nextButton.addEventListener("click", this.handleNextClick);

    this.slider.on("change", (index) => {
      if (this.state.active !== index) {
        this.setState({ active: index });
      }
      if (variableHeight) this.setNavHeight();
    });

    this.slider.on("lazyLoad", (event) => {
      event.target.classList.add("is-loaded");
      this.setNavHeight();
    });

    this.bindMessageEvent();
  }

  bindMessageEvent() {
    if (window.addEventListener) {
      window.addEventListener("message", this.handleGroupMessage, false);
    } else {
      window.attachEvent("onmessage", this.handleGroupMessage);
    }
  }

  handleGroupMessage = ({ data, origin }) => {
    if (!data || data[0] !== "group" || location.origin !== origin) return;
    const workId = data[1].workId;

    if (!workId) return;

    const slide = this.slides.filter((slide) => {
      return slide.dataset.workId === String(workId);
    });

    this.setState({ active: Number(slide[0].dataset.index) });
  };

  handlePreviousClick = (event) => {
    const { active } = this.state;

    const previousSlide = active === 0 ? this.slideCount - 1 : active - 1;
    this.setState({ active: previousSlide });
  };

  handleNextClick = (event) => {
    const { active } = this.state;
    const nextSlide = active === this.slideCount - 1 ? 0 : active + 1;
    this.setState({ active: nextSlide });
  };

  setNavHeight = () => {
    const img = this.activeImg;
    if (img) {
      this.refs.nav.style.setProperty(
        "--padding-top",
        `${(img.offsetHeight / img.offsetWidth) * 100}%`
      );
      this.refs.nav.style.setProperty("--height", `${img.offsetHeight}px`);
    }
  };
}
